interface DockRow {
  id: string;
  accountUserId: number | null;
  initiator: string;
  acknowledgedAt: string | null;
}

function eligible(row: DockRow, userId: number | undefined): boolean {
  return row.accountUserId === userId && row.initiator === 'ui' && row.acknowledgedAt === null;
}

function upsert<Row extends DockRow>(rows: Row[], row: Row, userId: number | undefined): Row[] {
  const rest = rows.filter(item => item.id !== row.id);
  return eligible(row, userId) ? [row, ...rest] : rest;
}

/** Each adapter owns its captures; all concurrent reads of its cache key receive the same durable row. */
export function createDockRowReconciler<Row extends DockRow>() {
  const duringRead = new Map<string, Set<Map<string, Row>>>();
  return {
    read: async (key: readonly unknown[], userId: number | undefined, fetchRows: () => Promise<Row[]>): Promise<Row[]> => {
      const captureKey = JSON.stringify(key);
      const reads = duringRead.get(captureKey) ?? new Set<Map<string, Row>>();
      const frames = new Map<string, Row>();
      reads.add(frames);
      duringRead.set(captureKey, reads);
      try {
        let rows = (await fetchRows()).filter(row => eligible(row, userId));
        for (const row of frames.values()) rows = upsert(rows, row, userId);
        return rows;
      } finally {
        reads.delete(frames);
        if (!reads.size) duringRead.delete(captureKey);
      }
    },
    put: (key: readonly unknown[], userId: number | undefined, rows: Row[], row: Row): Row[] => {
      for (const frames of duringRead.get(JSON.stringify(key)) ?? []) frames.set(row.id, row);
      return upsert(rows, row, userId);
    },
  };
}
